// Dependencies
import $axios, { setAuthToken } from '@martyrs/src/modules/core/views/utils/axios-instance.js';
import Cookies from 'js-cookie';
// Capacitor
import { Preferences } from '@capacitor/preferences';
// Vue modules
import { reactive } from 'vue';
// Globals
import { useSession, setError, defineStore } from '@martyrs/src/modules/core/views/store/core.store.js';
import wsManager from '@martyrs/src/modules/core/views/classes/ws.manager.js';

export const auth = defineStore('auth', () => {
  // State
  const initial = () => ({
    user: {
      _id: undefined,
      avatar: null,
      username: '',
      email: '',
      phone: '',
      password: '',
      passwordRepeat: '',
      locale: null,
      country: null,
    },
    access: {
      roles: null,
      token: null,
      status: false,
    },
    accesses: [],
  });

  const state = reactive(initial());

  // Промис текущей проверки куки на сервере (check-token). Состояние из куки
  // оптимистичное: гидрация ставит access.status до ответа сервера. Гард роутера
  // проекта ждёт этот промис через revalidated(), прежде чем решать по входу, —
  // иначе отозванная кука успевает увести со страницы входа в кабинет.
  // Живёт в замыкании, а не в state: state уходит в data-state, промис туда
  // не сериализуется. На сервере не ставится — там initialize() без сети.
  let revalidation = null;

  // Кука → личность в state, на клиенте затем проверка токена на сервере.
  // Отдаёт куку, когда сервер подтвердил токен; null — гость, сервер (SSR без
  // сети), отозванная кука или сбой сети. В промис входит лишь проверка и сброс
  // на 401/403 — accesses и WebSocket идут после, чтобы гард роутера их не ждал.
  async function confirmCookie(cookie) {
    const userCookie = cookie ? cookie : await getCookie('user');

    if (!userCookie) {
      setAuthToken(null);
      useSession().clear();
      actions.resetState();
      return null;
    }

    // Optimistic-гидрация из куки: сервер и так валидирует токен на каждом
    // API-запросе (authJwt), поэтому доверяем куке сразу. SSR при этом не делает
    // HTTP-запрос сам к себе (check-token) — валидация только на клиенте, фоном.
    const { _id, email, phone, avatar, roles, accessToken, locale, country } = userCookie;

    setAuthToken(accessToken);
    Object.assign(state.user, { _id, email, phone, avatar, locale, country });
    Object.assign(state.access, { token: accessToken, roles, status: !!accessToken });
    useSession().set(userCookie);

    if (typeof window === 'undefined') return null;

    // Валидация токена и сессии.
    return $axios.get('/api/auth/check-token').then(
      () => userCookie,
      async error => {
        // Кука стирается ТОЛЬКО когда токен/сессия реально невалидны (401/403).
        // Сетевые ошибки и 5xx (деплой, рестарт, поднимающаяся база) куку не трогают:
        // состояние остаётся из куки, следующая загрузка ревалидирует.
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          await removeCookie('user');
          setAuthToken(null);
          actions.resetState();
        } else {
          console.warn('Auth revalidation skipped (transient error):', error.message);
        }
        return null;
      }
    );
  }

  const actions = {
    async initialize(cookie) {
      // Промис ставится до первого await: гидрация зовёт initialize() без
      // ожидания и сразу ставит роутер, гард первой навигации спрашивает
      // revalidated() через несколько микрозадач. Промис, поставленный после
      // await getCookie, гард успевал бы увидеть только по порядку микрозадач.
      const confirmed = confirmCookie(cookie);
      if (typeof window !== 'undefined') revalidation = confirmed;

      const userCookie = await confirmed;
      if (!userCookie) return;

      const { _id, accessToken } = userCookie;

      try {
        // Accesses опциональны — их недоступность не повод разлогинивать.
        // Загрузчик регистрирует модуль полномочий (organizations); без него запроса нет.
        const accesses = accessesLoader ? await Promise.resolve(accessesLoader()).catch(() => []) : [];

        state.accesses = accesses;
        useSession().set({ ...userCookie, accesses });

        // Переподключаем WebSocket если пользователь аутентифицирован
        if (_id && accessToken) {
          await wsManager.reconnectWithAuth(_id);
        }
      } catch (error) {
        // Сбой сокета после подтверждённого токена куку не трогает.
        console.warn('Auth revalidation skipped (transient error):', error.message);
      }
    },

    // Проверка куки, запущенная initialize() на клиенте. Нет проверки (сервер,
    // initialize() ещё не звали) — сразу решённый промис.
    revalidated() {
      return revalidation || Promise.resolve();
    },

    // Логаут и потеря доступа: вернуть личность к начальным значениям в текущем
    // запросе (state запроса свой, но внутри запроса его надо очистить).
    resetState() {
      Object.assign(state, initial());
      useSession().clear();
    },

    async login(user, type) {
      try {
        const response = await $axios.post('/api/auth/signin', {
          ...user,
          type,
        });

        await applySignin(response.data);

        return response.data;
      } catch (error) {
        await dropSignin(error);
        return Promise.reject(error);
      }
    },

    // Вход по одноразовому коду. Первый вызов (без challengeId/otp) — сервер
    // шлёт код и отвечает `requires_otp`: состояние не трогаем, отдаём ответ
    // странице. Второй вызов (с challengeId и otp) — вход, как в login.
    async loginCode(user, type) {
      try {
        const response = await $axios.post('/api/auth/signin-code', {
          ...user,
          type,
        });

        if (response.data?.status === 'requires_otp') {
          return response.data;
        }

        await applySignin(response.data);

        return response.data;
      } catch (error) {
        await dropSignin(error);
        return Promise.reject(error);
      }
    },

    async signup(user, type, invite) {
      const { password, passwordRepeat } = state.user;


      if (password !== passwordRepeat) {
        const error = {
          response: {
            data: { errorCode: 'PASSWORDS_DO_NOT_MATCH' },
          },
        };
        setError(error);
        throw new Error(error.response.status);
      }

      try {
        const response = await $axios.post('/api/auth/signup', {
          ...user,
          type,
          inviteCode: invite,
        });


        if (response.data.accessToken) {
          setCookie('user', response.data);
        }

        Object.assign(state.user, {
          ...response.data,
        });

        Object.assign(state.access, {
          token: response.data.accessToken,
          status: true,
          roles: response.data.roles,
        });

        useSession().set(response.data);

        await actions.initialize();

        return response.data;
      } catch (error) {
        // Make sure cookie is removed on signup error
        await removeCookie('user');
        setError(error);
        throw error;
      }
    },

    async logout() {
      await removeCookie('user');
      setAuthToken(null);
      useSession().clear();
      actions.resetState();

      // Отключаем WebSocket при выходе
      wsManager.disconnect();
    },
  };

  // Успешный ответ входа (signin, signin-code): кука, личность, доступ,
  // сессия, инициализация. Одна на login и loginCode.
  async function applySignin(data) {
    setCookie('user', data);

    Object.assign(state.user, {
      ...data,
    });

    Object.assign(state.access, {
      token: data.accessToken,
      status: true,
      roles: data.roles,
    });

    useSession().set(data);

    await actions.initialize();
  }

  // Ошибка входа: снять куку и доступ, показать ошибку.
  async function dropSignin(error) {
    // Make sure cookie is removed on login error
    await removeCookie('user');

    Object.assign(state.access, {
      token: null,
      status: false,
      roles: null,
    });

    useSession().clear();

    setError(error);
  }

  return { state, actions };
});

// Accesses-провайдер регистрирует внешний модуль (organizations) при своей
// инициализации: auth отвечает за личность и не знает, кто выдаёт полномочия.
// Нет провайдера — accesses остаются [] и никаких запросов не делается.
// Загрузчик один на процесс (это функция запроса к API, а не состояние
// пользователя), поэтому живёт вне фабрики стора.
let accessesLoader = null;

export function registerAccessesLoader(loader) {
  accessesLoader = loader;

  // Модули грузятся лениво по маршруту: организация может зарегистрироваться
  // ПОСЛЕ активации модуля auth. Тогда подтягиваем accesses сразу при регистрации.
  if (typeof window !== 'undefined' && auth.state.access.status && !auth.state.accesses.length) {
    (async () => {
      try {
        const accesses = await loader();
        auth.state.accesses = accesses;
        const userCookie = await getCookie('user');
        // useSession() зовём по месту, а не константой на уровне модуля: на
        // сервере модуль импортируется вне AsyncLocalStorage, там useStore()
        // отдал бы одноразовый стор и сессия писалась бы мимо стора запроса.
        if (userCookie) useSession().set({ ...userCookie, accesses });
      } catch (e) {
        // Недоступность accesses не критична — останутся []
      }
    })();
  }
}

// Cookies
const optionsDefault = {
  development: { path: '/', domain: process.env.DOMAIN_URL, secure: false, expires: 30, sameSite: 'Lax' },
  production: {
    expires: 30,
    path: '/',
    domain: process.env.DOMAIN_URL,
    sameSite: 'Lax',
    secure: true,
  },
};

export async function getCookie(name) {
  if (process.env.MOBILE_APP) {
    const { value } = await Preferences.get({ key: name });
    return value ? JSON.parse(value) : null;
  } else {
    const cookie = Cookies.get(name);
    return cookie ? JSON.parse(cookie) : null;
  }
}

export async function setCookie(name, data, mode = process.env.NODE_ENV) {
  if (process.env.MOBILE_APP) {
    // Если приложение запущено в Capacitor
    await Preferences.set({
      key: name,
      value: JSON.stringify(data),
    });
  } else {
    // Для веб-версии
    const options = optionsDefault[mode];
    Cookies.set(name, JSON.stringify(data), options);
  }
}

export async function removeCookie(name, mode = process.env.NODE_ENV) {
  if (process.env.MOBILE_APP) {
    // Если приложение запущено в Capacitor
    await Preferences.remove({ key: name });
  } else {
    // Для веб-версии
    const options = optionsDefault[mode];
    Cookies.remove(name, options);
    // Кука с доменом и кука без домена — для браузера две разные куки.
    // Регистрация и сброс пароля раньше ставили `user` сами, без домена, и
    // удаление с доменом её не трогало: отозванная сессия оставалась в куке,
    // каждое обновление страницы снова считало человека вошедшим. Стираем и
    // этот вариант, пока такие куки живут у людей (срок — 30 дней).
    Cookies.remove(name, { path: options.path });
  }
}
